import { useTranslation } from "react-i18next";
// src/pages/ProductDetail.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiDelete, apiGet, apiPut } from "../api/client";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { companyRoute } from "../utils/routeHelpers";
import { Button } from "../components/Button";
import { formatNumber, formatMoney } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";

type Product = {
  id: number;
  name: string;
  sku?: string | null;
  barcode?: string | null;
  sell_price?: number | null;
  is_service?: boolean;
  // Backend-computed: true when the product has inventory/purchase/sale
  // history, which freezes the Inventory/Non-inventory classification
  // (INV-SERVICE-1).
  type_locked?: boolean;
};

type Draft = {
  name: string;
  sku: string;
  barcode: string;
  sell_price: string; // controlled input
  is_service: boolean;
};

function cleanStr(s: string) {
  return String(s ?? "").trim();
}

function dashIfEmpty(v?: string | null) {
  return v && String(v).trim() ? String(v) : "—";
}

function sanitizeMoney(value: string) {
  // allow typing like "1,234.5" → keep digits + 1 dot, limit to 2dp
  let v = String(value ?? "").replace(/[^0-9.]/g, "");
  const parts = v.split(".");
  if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
  if (parts[1]?.length > 2) v = parts[0] + "." + parts[1].slice(0, 2);
  return v;
}

function to2dpNumber(s: string) {
  const n = parseFloat(String(s || "0"));
  if (isNaN(n) || n < 0) return 0;
  return Math.round(n * 100) / 100;
}

function money(n?: number | null) {
  const num = Number(n ?? 0);
  return formatMoney(num);
}

type DisplayError = { local: "nameRequired" | "missingId" | "loadDetailFailed" | "saveFailed" | "deleteFailed" } | { raw: string } | null;

export default function ProductDetail() {
  const { t } = useTranslation();
  const errorText = (error: DisplayError): string | null => error == null ? null : "raw" in error ? error.raw : ({
    nameRequired: t("directory.products.nameRequired"),
    missingId: t("directory.products.missingId"),
    loadDetailFailed: t("directory.products.loadDetailFailed"),
    saveFailed: t("directory.products.saveFailed"),
    deleteFailed: t("directory.products.deleteFailed"),
  })[error.local];

  const params = useParams<Record<string, string | undefined>>();
  const navigate = useNavigate();

  const company_id = params.company_id;

  // tolerate different route param names to prevent “stuck loading”
  const routeId =
    params.id ?? params.product_id ?? params.productId ?? params.productID;

  const listPath = useMemo(
    () => (company_id ? companyRoute(company_id, "/products") : "/products"),
    [company_id],
  );

  const withCompany = (path: string) =>
    company_id ? `${path}?company_id=${encodeURIComponent(company_id)}` : path;

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<DisplayError>(null);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({
    name: "",
    sku: "",
    barcode: "",
    sell_price: "",
    is_service: false,
  });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<DisplayError>(null);

  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<DisplayError>(null);

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    `hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`;

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!routeId) {
        setLoadError({ local: "missingId" });
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);

      try {
        const data = await apiGet<Product>(withCompany(`/products/${routeId}`));
        if (cancelled) return;

        setProduct(data);
        setDraft({
          name: data.name ?? "",
          sku: data.sku ?? "",
          barcode: data.barcode ?? "",
          sell_price:
            typeof data.sell_price === "number"
              ? data.sell_price.toFixed(2)
              : "",
          is_service: !!data.is_service,
        });
      } catch (e: any) {
        const msg =
          e?.response?.data?.detail || e?.message;
        if (!cancelled) setLoadError(msg ? { raw: msg } : { local: "loadDetailFailed" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [routeId, company_id]);

  const header = (
    <div className="sticky top-0 z-30 bg-gray-50 border-b border-gray-100 px-2 pb-2 mb-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtn}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
        </button>

        <h2 className={`flex-1 text-center ${A.title}`}>{t("directory.products.detail")}</h2>

        <button
          type="button"
          onClick={() => navigate(listPath)}
          className={iconBtn}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="h-5 w-5 text-gray-500" />
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={`min-h-screen bg-gray-50 ${A.body}`}>
        <div className="w-full max-w-xl mx-auto px-2 sm:px-6 pt-4 pb-10">
          {header}
          <div className={`${A.card} p-5 sm:p-6 text-gray-500`}>{t("directory.common.loading")}</div>
        </div>
      </div>
    );
  }

  if (loadError || !product) {
    return (
      <div className={`min-h-screen bg-gray-50 ${A.body}`}>
        <div className="w-full max-w-xl mx-auto px-2 sm:px-6 pt-4 pb-10">
          {header}
          <div className={`${A.card} p-5 sm:p-6 text-red-500`}>
            {errorText(loadError) ?? t("directory.products.notFound")}
          </div>
        </div>
      </div>
    );
  }

  const productId = product.id;

  const beginEdit = () => {
    setSaveError(null);
    setDraft({
      name: product.name ?? "",
      sku: product.sku ?? "",
      barcode: product.barcode ?? "",
      sell_price:
        typeof product.sell_price === "number"
          ? product.sell_price.toFixed(2)
          : "",
      is_service: !!product.is_service,
    });
    setEditing(true);
  };

  async function handleSave() {
    if (saving) return;

    setSaving(true);
    setSaveError(null);

    const payload = {
      name: cleanStr(draft.name),
      sku: cleanStr(draft.sku) ? cleanStr(draft.sku) : null,
      barcode: cleanStr(draft.barcode) ? cleanStr(draft.barcode) : null,
      sell_price: to2dpNumber(draft.sell_price),
      is_service: draft.is_service,
    };

    if (!payload.name) {
      setSaving(false);
      setSaveError({ local: "nameRequired" });
      return;
    }

    try {
      await apiPut(withCompany(`/products/${productId}`), payload);
      setProduct((prev) => (prev ? { ...prev, ...payload } : prev));
      setEditing(false);
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail || e?.message;
      setSaveError(msg ? { raw: msg } : { local: "saveFailed" });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (deleting) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await apiDelete(withCompany(`/products/${productId}`));
      navigate(listPath, { replace: true });
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail || e?.message;
      setDeleteError(msg ? { raw: msg } : { local: "deleteFailed" });
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      <div className="w-full max-w-xl mx-auto px-2 sm:px-6 pt-4 pb-10">
        {header}

        <div className={`${A.card} p-5 sm:p-6`}>
          {/* Hero */}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className={`${A.title} truncate`}>{product.name}</div>
              <div className={`${A.label} truncate`}>
                {product.sku?.trim() ? t("directory.products.skuPrefix", { sku: product.sku }) : t("directory.products.noSku")}
              </div>
            </div>
          </div>

          <div className="h-px bg-gray-100 my-4" />

          {/* Content */}
          {editing ? (
            <div className="space-y-3">
              <Field label={t("directory.common.name")}>
                <input
                  className={A.inputBase}
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, name: e.target.value }))
                  }
                  autoFocus
                  disabled={saving}
                />
              </Field>
              <Field label={t("directory.products.type")}>
                <div className="inline-flex w-full rounded-xl border border-gray-200 bg-gray-50 p-1">
                  <button
                    type="button"
                    onClick={() =>
                      setDraft((d) => ({ ...d, is_service: false }))
                    }
                    disabled={saving || !!product.type_locked}
                    className={[
                      "flex-1 px-3 py-1.5 text-[13px] font-medium rounded-lg transition",
                      "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                      product.type_locked ? "cursor-not-allowed opacity-60" : "",
                      !draft.is_service
                        ? "bg-white shadow-sm text-gray-900"
                        : "text-gray-500 hover:text-gray-700",
                    ].join(" ")}
                  >
                    {t("directory.products.inventory")}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setDraft((d) => ({ ...d, is_service: true }))
                    }
                    disabled={saving || !!product.type_locked}
                    className={[
                      "flex-1 px-3 py-1.5 text-[13px] font-medium rounded-lg transition",
                      "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                      product.type_locked ? "cursor-not-allowed opacity-60" : "",
                      draft.is_service
                        ? "bg-white shadow-sm text-gray-900"
                        : "text-gray-500 hover:text-gray-700",
                    ].join(" ")}
                  >
                    {t("directory.products.nonInventory")}
                  </button>
                </div>
                {product.type_locked && (
                  <p className="mt-1.5 text-[12px] text-gray-500">
                    {t("directory.products.typeLocked")}
                  </p>
                )}
              </Field>
              <Field label={t("directory.products.sku")}>
                <input
                  className={A.inputBase}
                  value={draft.sku}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, sku: e.target.value }))
                  }
                  disabled={saving}
                />
              </Field>

              <Field label={t("directory.products.barcode")}>
                <input
                  className={A.inputBase}
                  value={draft.barcode}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, barcode: e.target.value }))
                  }
                  disabled={saving}
                />
              </Field>

              <Field label={t("directory.products.sellPrice")}>
                <input
                  className={`${A.inputBase} text-right ${A.num}`}
                  value={draft.sell_price}
                  inputMode="decimal"
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      sell_price: sanitizeMoney(e.target.value),
                    }))
                  }
                  onBlur={() =>
                    setDraft((d) => {
                      if (!d.sell_price) return d;
                      const n = to2dpNumber(d.sell_price);
                      return { ...d, sell_price: n.toFixed(2) };
                    })
                  }
                  disabled={saving}
                />
              </Field>
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-100 overflow-hidden">
              <div className="divide-y divide-gray-100">
                <InfoRow label={t("directory.common.name")} value={dashIfEmpty(product.name)} />
                <InfoRow
                  label={t("directory.products.type")}
                  value={product.is_service ? t("directory.products.nonInventory") : t("directory.products.inventory")}
                />
                <InfoRow label={t("directory.products.sku")} value={dashIfEmpty(product.sku)} />
                <InfoRow label={t("directory.products.barcode")} value={dashIfEmpty(product.barcode)} />
                <InfoRow
                  label={t("directory.products.sellPrice")}
                  value={money(product.sell_price)}
                  mono
                />
              </div>
            </div>
          )}

          {/* Actions INSIDE card */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="min-h-[18px] text-right">
              {saveError ? (
                <div className="text-xs text-red-500 font-medium">
                  {errorText(saveError)}
                </div>
              ) : null}
            </div>

            <div className="flex justify-end gap-3">
              {!editing ? (
                <>
                  <Button
                    variant="outline"
                    type="button"
                    onClick={beginEdit}
                    className="border-appleblue text-appleblue hover:bg-appleblue/5 focus:outline-none focus:ring-2 focus:ring-appleblueD"
                  >
                    {t("directory.common.edit")}
                  </Button>

                  {/* Delete default = secondary/gray */}
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setShowDelete(true);
                      setDeleteError(null);
                    }}
                  >
                    {t("directory.common.delete")}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setSaveError(null);
                    }}
                    disabled={saving}
                  >
                    {t("directory.common.cancel")}
                  </Button>

                  <Button
                    variant="primary"
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !draft.name.trim()}
                  >
                    {saving ? t("directory.common.savingEllipsis") : t("directory.common.save")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {showDelete ? (
          <ConfirmDelete
            title={t("directory.products.deleteConfirm")}
            subtitle={t("directory.common.irreversible")}
            busy={deleting}
            error={errorText(deleteError)}
            onCancel={() => {
              setShowDelete(false);
              setDeleteError(null);
            }}
            onConfirm={handleDelete}
          />
        ) : null}
      </div>
    </div>
  );
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className={`${A.label} mb-1`}>{props.label}</div>
      {props.children}
    </div>
  );
}

function InfoRow(props: { label: string; value: string; mono?: boolean }) {
  const isDash = props.value === "—";

  return (
    <div className="grid grid-cols-12 gap-3 px-4 py-3">
      <div className={`col-span-4 ${A.label} text-left`}>{props.label}</div>

      <div
        className={
          "col-span-8 text-left" + // ✅ force center for ALL values
          (props.mono ? `${A.num} font-medium` : "truncate") +
          (isDash ? " text-gray-400 italic" : " text-gray-700")
        }
      >
        {props.value}
      </div>
    </div>
  );
}

function ConfirmDelete(props: {
  title: string;
  subtitle: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-3">
      <div className={`${A.modal} shadow-md w-full max-w-sm p-4`}>
        <div className={A.title}>{props.title}</div>
        <div className="mt-1 text-[13px] text-gray-600">{props.subtitle}</div>

        <div className="min-h-[18px] mt-2">
          {props.error ? (
            <div className="text-xs text-red-500 font-medium">
              {props.error}
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button
            variant="secondary"
            type="button"
            onClick={props.onCancel}
            disabled={props.busy}
          >
            {t("directory.common.cancel")}
          </Button>

          <Button
            variant="primary"
            type="button"
            onClick={props.onConfirm}
            disabled={props.busy}
            className="bg-red-600 hover:bg-red-700 active:bg-red-800"
          >
            {props.busy ? t("directory.common.deleting") : t("directory.common.delete")}
          </Button>
        </div>
      </div>
    </div>
  );
}
